import React from 'react';
import { Sparkles, Euro, Coins, Clock, AlertCircle } from 'lucide-react';
import { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, PieChart, Pie, Cell } from 'recharts';

export default function DashboardTab({
  receitaMensalEsperada,
  totalRendasPagasGlobal,
  totalRendasPrevistasGlobal,
  totalRendasPendentesGlobal,
  PROPRIEDADES,
  stays,
  setSelectedPropriedade,
  setActiveTab,
  monthlyFlowData,
  chartPieData
}) {
  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between hover:shadow-md transition-all">
          <div>
            <p className="text-xs font-bold text-slate-400 tracking-wider uppercase">
              Faturamento Ativo Estimado
            </p>
            <p className="text-3xl font-black text-slate-900 mt-1">
              {receitaMensalEsperada.toLocaleString("pt-PT", {
                style: "currency",
                currency: "EUR",
              })}
            </p>
            <span className="text-[11px] text-slate-500 font-bold flex items-center gap-1 mt-1.5">
              <Sparkles className="h-3.5 w-3.5 text-yellow-500" /> 11 Apartamentos Coolivin
            </span>
          </div>
          <div className="p-4 bg-slate-50 text-slate-700 rounded-2xl">
            <Euro className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between hover:shadow-md transition-all">
          <div>
            <p className="text-xs font-bold text-slate-400 tracking-wider uppercase">
              Total Validado / Pago
            </p>
            <p className="text-3xl font-black text-emerald-600 mt-1">
              {totalRendasPagasGlobal.toLocaleString("pt-PT", {
                style: "currency",
                currency: "EUR",
              })}
            </p>
            <p className="text-[11px] text-slate-400 font-semibold mt-1.5">
              Total previsto:{" "}
              <strong className="text-slate-700">
                {totalRendasPrevistasGlobal.toLocaleString("pt-PT", {
                  style: "currency",
                  currency: "EUR",
                })}
              </strong>
            </p>
          </div>
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl">
            <Coins className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between hover:shadow-md transition-all">
          <div>
            <p className="text-xs font-bold text-slate-400 tracking-wider uppercase">
              Faltas Globais por Receber
            </p>
            <p className="text-3xl font-black text-rose-500 mt-1">
              {totalRendasPendentesGlobal.toLocaleString("pt-PT", {
                style: "currency",
                currency: "EUR",
              })}
            </p>
            <p className="text-[11px] text-rose-600 font-semibold mt-1.5 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Faltam liquidar tranches
            </p>
          </div>
          <div className="p-4 bg-rose-50 text-rose-500 rounded-2xl">
            <Clock className="h-6 w-6" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
        <h3 className="text-base font-extrabold text-slate-900 mb-6">
          Estado de Ocupação das 11 Casas
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {PROPRIEDADES.map((prop) => {
            const actives = stays.filter(
              (s) => s.propriedade === prop && s.status === "Ativo"
            ).length;
            const reservs = stays.filter(
              (s) => s.propriedade === prop && s.status === "Reservado"
            ).length;
            return (
              <div
                key={prop}
                onClick={() => {
                  setSelectedPropriedade(prop);
                  setActiveTab("properties");
                }}
                className="p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all cursor-pointer text-center group"
              >
                <p className="text-xs font-bold text-slate-700 group-hover:text-indigo-955 truncate">
                  {prop}
                </p>
                <div className="mt-3 flex justify-center gap-2">
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-black rounded-md">
                    {actives}
                  </span>
                  {reservs > 0 && (
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-black rounded-md">
                      {reservs}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 lg:col-span-2">
          <h3 className="text-base font-extrabold text-slate-900 mb-4">
            Projeção Mensal de Faturamento
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyFlowData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  formatter={(value) =>
                    `${parseFloat(String(value)).toFixed(2)}€`
                  }
                />
                <Area
                  type="monotone"
                  dataKey="Receitas"
                  stroke="#0f172a"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#chartIncomeColor)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-extrabold text-slate-900 mb-4">
              Distribuição das Rendas
            </h3>
          </div>
          <div className="h-56 flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={85}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {chartPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}€`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute text-center">
              <p className="text-2xl font-black text-slate-900">
                {receitaMensalEsperada}€
              </p>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                Mês Core
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 mt-4 text-xs max-h-40 overflow-y-auto pr-1">
            {chartPieData.map((item) => (
              <div
                key={item.name}
                className="flex items-center justify-between py-0.5 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  <span className="text-slate-600 font-semibold">
                    {item.name}
                  </span>
                </div>
                <span className="text-slate-955 font-extrabold">
                  {item.value}€ ({Math.round((item.value / (receitaMensalEsperada || 1)) * 105)}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
